<template>
  <div class="sl-v2" @keydown="onKey" tabindex="0">
    <div class="ws-head">
      <div class="ws-field sl-pick">
        <div class="flab">Smartlink</div>
        <div class="sl-picker-wrap">
          <button type="button" class="ctl sl-picker sl-picker-btn" @click.stop="togglePicker">
            <span class="sl-picker-val">
              <span class="sl-picker-id">{{ pickerId || '—' }}</span>
              <span class="sl-pick-name">{{ currentPickerName || 'Select a smartlink' }}</span>
            </span>
            <span class="sl-picker-caret">{{ pickerOpen ? '▴' : '▾' }}</span>
          </button>
          <div v-if="pickerOpen" class="sl-picker-menu" @click.stop>
            <input
              ref="pickerSearch"
              class="sl-picker-search"
              v-model="pickerQ"
              type="text"
              placeholder="Search ID or name…"
            >
            <div class="sl-picker-list">
              <button
                v-for="p in filteredPicker"
                :key="'pk'+p.id"
                type="button"
                class="sl-picker-opt"
                :class="{ on: Number(p.id) === Number(pickerId) }"
                @click="chooseSL(p.id)"
              >
                <span class="sl-picker-id">{{ p.id }}</span>
                <span class="sl-pick-name">{{ p.name }}</span>
              </button>
              <div v-if="!filteredPicker.length" class="sl-picker-empty">No matches</div>
            </div>
          </div>
        </div>
      </div>
      <SlV2Status v-if="tree" :sl="statusModel" @changed="loadTree" />
      <div class="ws-field gcell" v-if="tree">
        <div class="flab">Group</div>
        <select :value="tree.group_id" @change="setGroup($event)">
          <option v-for="g in groupList" :key="g.id" :value="g.id">{{ g.name }}</option>
        </select>
      </div>
      <span v-if="dirtyCount" class="dirty">{{ dirtyCount }} unsaved</span>
      <div class="grow"></div>
      <button class="btn" type="button" :disabled="!tree" @click="openActivity">Activity</button>
      <button class="btn" type="button" :disabled="!dirtyCount || saving" @click="undo">Undo</button>
      <button class="btn primary" type="button" :disabled="!dirtyCount || saving" @click="save">Save</button>
    </div>

    <div v-if="!tree" class="empty">Loading workspace…</div>
    <template v-else>
      <div class="slpanes">
      <div class="acc flat" :class="{ open: openSetup }">
        <div class="acc-h" @click="openSetup = !openSetup">
          <span class="acc-car">▶</span>
          <span class="acc-t">Smart link setup</span>
          <div class="acc-sum">
            <span class="sm">Frq <b>{{ freqLbl(tree.frequency) }}</b></span>
            <span class="sm">IP Frq <b>{{ freqLbl(tree.ip_frequency) }}</b></span>
            <span class="sm">{{ ipLevelLbl(tree.ip_frequency_level) }}</span>
            <span class="sm" :class="{ warn: setupRuleStats.n }">Subs <b>{{ setupRuleStats.n }}</b> rule{{ setupRuleStats.n === 1 ? '' : 's' }}{{ setupRuleStats.n ? ' · ' + setupRuleStats.vals + ' values' : '' }}</span>
            <span class="sm">Fallback <b>{{ tree.fallback_url ? 'set' : 'default' }}</b></span>
          </div>
        </div>
        <div class="acc-b" v-show="openSetup">
          <div class="fgrid">
            <div class="frow">
              <div class="flab">Name</div>
              <div class="fbody"><input class="ti w-xl" :value="tree.name" :title="tree.name" @input="patchSL('name', $event.target.value)"></div>
            </div>
            <div class="frow">
              <div class="flab">IP Frequency Level</div>
              <div class="fbody">
                <select class="ti" :value="tree.ip_frequency_level" @change="patchSL('ip_frequency_level', $event.target.value)">
                  <option v-for="l in ipLevels" :key="l.value" :value="l.value">{{ l.text }}</option>
                </select>
                <button class="qmark" type="button" title="Composite key precision. Level 1 = broadest, Level 5 = strictest." @click.stop="showFrqLegend = true">?</button>
              </div>
            </div>
            <div class="frow">
              <div class="flab">Frequency</div>
              <div class="fbody"><FreqSeg :value="tree.frequency" @input="patchSL('frequency', $event)" /></div>
            </div>
            <div class="frow">
              <div class="flab">IP Frequency</div>
              <div class="fbody"><FreqSeg :value="tree.ip_frequency" @input="patchSL('ip_frequency', $event)" /></div>
            </div>
            <div class="frow full">
              <div class="flab">Fallback URL</div>
              <div class="fbody">
                <input class="ti w-xl" :value="tree.fallback_url" @input="patchSL('fallback_url', $event.target.value)">
                <span class="hint">default: https://google.com</span>
              </div>
            </div>
            <div class="frow full">
              <div class="flab">Campaign Link</div>
              <div class="fbody">
                <input class="ti w-xl link-ro" :value="campLink" readonly>
                <button class="btn btn-s" type="button" @click="copyCampLink">Copy</button>
              </div>
            </div>
            <div class="frow full">
              <div class="flab">Subs Blacklisting</div>
              <div class="fbody" style="flex-direction:column;align-items:stretch">
                <SubRulesEditor :rules="tree.sub_rules" @change="markSubRules('smart_link', tree.id)" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="acc flat locked">
        <div class="acc-h">
          <span class="acc-t">Statistics</span>
          <div class="acc-sum">
            <span class="sm">Requests <b>{{ fmtNum(st.requests) }}</b>
              <button
                v-if="userHasPermission('all-requests-report') && st.requests"
                class="dl"
                type="button"
                title="Download requests"
                @click="generateReport('requests')"
              >⤓</button>
            </span>
            <span class="sm">Served <b>{{ fmtNum(st.served) }}</b></span>
            <span
              class="sm lostn"
              style="color:var(--red)"
              @mouseenter="showPop($event)"
              @mouseleave="hidePopSoon"
            >Lost <b>{{ fmtNum(st.lost) }}</b> · {{ fmtPct(st.lost_pct) }}
              <button
                v-if="st.lost"
                class="dl"
                type="button"
                title="Download lost"
                @click.stop="generateReport('blocked')"
              >⤓</button>
            </span>
          </div>
          <div class="grow"></div>
          <div class="ctl stat-ctl" @click.stop>
            <date-range-picker
              v-model="dateRange"
              opens="left"
              :locale-data="{ firstDay: 1, format: 'dd.mm.yyyy' }"
              :ranges="ranges"
              :maxDate="maxDate"
              @update="onStatRangeUpdate"
            />
          </div>
          <button class="btn btn-g btn-s" type="button" :disabled="statsLoading" @click.stop="refreshStats">↻ Refresh</button>
        </div>
      </div>
      </div>

      <div class="ws-layout">
        <div class="rail" :class="{ mini: railCollapsed }">
          <div class="rail-h">
            <button class="railtog" type="button" :title="railCollapsed ? 'Expand' : 'Collapse'" @click="railCollapsed = !railCollapsed">{{ railCollapsed ? '›' : '‹' }}</button>
            <span class="rail-t">Ad sets</span>
            <span class="rail-hint">waterfall</span>
          </div>
          <div class="rail-l">
            <div
              v-for="(as, idx) in tree.ad_sets"
              :key="as.id"
              :class="[railCollapsed ? 'ascm' : 'asc', { on: as.id === currentId }]"
              :title="railCollapsed ? asRailTitle(as, idx) : null"
              draggable="true"
              @click="currentId = as.id"
              @dragstart="onRailDrag(idx)"
              @dragover.prevent
              @drop="onRailDrop(idx)"
            >
              <template v-if="railCollapsed">
                <span class="n">{{ idx + 1 }}</span>
                <span class="c">{{ asOfferCount(as) }}</span>
              </template>
              <template v-else>
                <span class="aso">{{ idx + 1 }}</span>
                <span class="grip" title="Drag to reorder">⠿</span>
                <div class="asn">{{ as.name }}</div>
                <div class="ast">{{ as.id }} · {{ as.geo || '—' }} · {{ as.os || '—' }}</div>
                <div class="asb">
                  <span class="mini-t">{{ asActiveCount(as) }}/{{ asOfferCount(as) }} on</span>
                  <span class="mini-t">W {{ asWeightTotal(as) }}</span>
                  <span class="mini-t">F {{ freqLbl(as.frequency) }}</span>
                  <span v-if="asAutoCount(as)" class="mini-t aw">auto {{ asAutoCount(as) }}</span>
                  <span v-if="asSubCount(as)" class="mini-t sb">sub {{ asSubCount(as) }}</span>
                </div>
              </template>
            </div>
          </div>
          <div class="rail-f">
            <button class="btn" type="button" @click="createAdSet">{{ railCollapsed ? '+' : '+ New ad set' }}</button>
          </div>
        </div>

        <div class="work" v-if="current">
          <div class="acc" :class="{ open: openAdset }">
            <div class="acc-h" @click="openAdset = !openAdset">
              <span class="acc-car">▶</span>
              <span class="acc-t">Ad set setup</span>
              <div class="acc-sum">
                <span class="sm">Geo <b>{{ geoCode(current.geo) || '—' }}</b></span>
                <span class="sm">Region <b>{{ current.region || '—' }}</b></span>
                <span class="sm">OS <b>{{ osSum(current.os) }}</b></span>
                <span class="sm">Conn <b>{{ connSum(current.connection_type) }}</b></span>
                <span class="sm">Traffic <b>{{ trafficSum(current) }}</b></span>
                <span class="sm">Creative <b>{{ creativeSum(current) }}</b></span>
                <span class="sm">Frq <b>{{ freqLbl(current.frequency) }}</b></span>
                <span class="sm">IP Frq <b>{{ freqLbl(current.ip_frequency) }}</b></span>
                <span class="sm">Cap <b>{{ current.clicks_cap || 0 }}</b></span>
                <span v-if="asUsesBundles(current)" class="sm">Bundles</span>
                <span v-if="current.use_offer_rotation" class="sm">Rotation</span>
              </div>
            </div>
            <div class="acc-b" v-show="openAdset">
              <div class="fgrid">
                <div class="frow">
                  <div class="flab">Geo</div>
                  <div class="fbody">
                    <GeoRegionSelect :key="'geo-' + current.id" kind="geo" :value="current.geo" @input="onGeoChange" />
                  </div>
                </div>
                <div class="frow">
                  <div class="flab">Region</div>
                  <div class="fbody">
                    <GeoRegionSelect
                      :key="'region-' + current.id + '-' + geoCode(current.geo)"
                      kind="region"
                      :geo="current.geo"
                      :value="current.region"
                      :disabled="!regionGeoOk(current.geo)"
                      @input="patchAS(current, 'region', $event)"
                    />
                  </div>
                </div>
                <div class="frow-group">
                  <div class="frow">
                    <div class="flab">OS</div>
                    <div class="fbody">
                      <div class="fseg">
                        <button type="button" :class="{ on: osFlags(current.os).ios }" @click="toggleASOs('ios')">iOS</button>
                        <button type="button" :class="{ on: osFlags(current.os).android }" @click="toggleASOs('android')">Android</button>
                      </div>
                    </div>
                  </div>
                  <div class="frow">
                    <div class="flab">Connection type</div>
                    <div class="fbody">
                      <div class="fseg">
                        <button type="button" :class="{ on: connFlags(current.connection_type).wifi }" @click="toggleASConn('wifi')">WiFi</button>
                        <button type="button" :class="{ on: connFlags(current.connection_type).cell }" @click="toggleASConn('cell')">Cellular</button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="frow-group">
                  <div class="frow">
                    <div class="flab">Traffic type</div>
                    <div class="fbody">
                      <div class="fseg">
                        <button type="button" :class="{ on: !!current.traffic_in_app }" @click="toggleASBit('traffic_in_app', 'traffic_mobile')">In-App</button>
                        <button type="button" :class="{ on: !!current.traffic_mobile }" @click="toggleASBit('traffic_mobile', 'traffic_in_app')">Mobile Web</button>
                      </div>
                    </div>
                  </div>
                  <div class="frow">
                    <div class="flab">Creative type</div>
                    <div class="fbody">
                      <div class="fseg">
                        <button type="button" :class="{ on: !!current.creative_tag }" @click="toggleASBit('creative_tag', 'creative_img')">TAG</button>
                        <button type="button" :class="{ on: !!current.creative_img }" @click="toggleASBit('creative_img', 'creative_tag')">IMG</button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="frow full">
                  <div class="flab">Bundles list</div>
                  <div class="fbody">
                    <label class="ckl">
                      <input class="cb" type="checkbox" :checked="asUsesBundles(current)" :disabled="!canEditBundles" @change="toggleASBundles(current, $event.target.checked)">
                      Use bundles list
                    </label>
                    <select class="ti w-lg" :value="current.bundles_lists_id || 0" :disabled="!canEditBundles || !asUsesBundles(current)" @change="patchAS(current, 'bundles_lists_id', Number($event.target.value))">
                      <option :value="0">No Bundle List</option>
                      <option v-for="item in bundlesLists" :key="item.id" :value="item.id">{{ item.list_name }}</option>
                    </select>
                  </div>
                </div>
                <div class="frow">
                  <div class="flab">Offer Rotation:</div>
                  <div class="fbody">
                    <label class="ckl">
                      <input class="cb" type="checkbox" :checked="!!current.use_offer_rotation" @change="patchAS(current, 'use_offer_rotation', !!$event.target.checked)">
                      Use Offer Rotation
                    </label>
                  </div>
                </div>
                <div class="frow">
                  <div class="flab">Clicks cap</div>
                  <div class="fbody"><input class="ti w-lg" type="number" :value="current.clicks_cap" @input="patchAS(current, 'clicks_cap', Number($event.target.value))" @wheel.prevent></div>
                </div>
                <div class="frow">
                  <div class="flab">Frequency</div>
                  <div class="fbody"><FreqSeg :value="current.frequency" @input="patchAS(current, 'frequency', $event)" /></div>
                </div>
                <div class="frow">
                  <div class="flab">IP Frequency</div>
                  <div class="fbody"><FreqSeg :value="current.ip_frequency" @input="patchAS(current, 'ip_frequency', $event)" /></div>
                </div>
                <div class="frow">
                  <div class="flab">IP Freq level</div>
                  <div class="fbody">
                    <select class="ti w-md" :value="current.ip_frequency_level" @change="patchAS(current, 'ip_frequency_level', $event.target.value)">
                      <option v-for="l in ipLevels" :key="'as'+l.value" :value="l.value">{{ l.text }}</option>
                    </select>
                    <button class="qmark" type="button" @click.stop="showFrqLegend = true">?</button>
                  </div>
                </div>
                <div class="frow">
                  <div class="flab">Comments</div>
                  <div class="fbody"><input class="ti w-lg" :value="current.comments" @input="patchAS(current, 'comments', $event.target.value)"></div>
                </div>
              </div>
              <div style="margin-top:8px;display:flex;gap:8px">
                <button class="btn" type="button" @click="duplicateAdSet">Duplicate</button>
                <button class="btn" type="button" @click="deleteAdSet">Delete</button>
              </div>
            </div>
          </div>

          <div class="panel" :class="{ editing: editTable }">
            <div class="banner" v-if="tree.blocked_reason">{{ tree.blocked_reason }} — this ad set is not serving.</div>
            <div class="ph">
              <span class="pt">Offers</span>
              <span class="ps">{{ (current.offers || []).length }} in ad set</span>
              <span class="grow"></span>
              <span class="lockmsg" :class="{ on: editTable }">{{ editTable ? '✎ Inline editing unlocked' : '🔒 Table is read-only' }}</span>
              <div class="sw" :class="{ on: editTable }" @click="editTable = !editTable">
                <span class="lbl">{{ editTable ? 'Editing' : 'Edit table' }}</span>
                <span class="track"><span class="knob"></span></span>
              </div>
              <div class="colsel">
                <button class="btn btn-g btn-s" type="button" @click.stop="colMenuOpen = !colMenuOpen">Columns ▾</button>
                <div class="colmenu" :class="{ on: colMenuOpen }">
                  <label v-for="c in offerColDefs" :key="c.k">
                    <input class="cb" type="checkbox" :checked="colOn(c.k)" @change="setOfferCol(c.k, $event.target.checked)"> {{ c.l }}
                  </label>
                </div>
              </div>
            </div>
            <div class="otbl-wrap">
              <table class="otable">
                <thead>
                  <tr>
                    <th class="oc-chk"><input class="cb" type="checkbox" :checked="allOffersSelected" @change="toggleAllOffers"></th>
                    <th class="oc-del"></th>
                    <th v-if="colOn('id')" class="oc-id">ID</th>
                    <th v-if="colOn('affise')" class="wrap oc-aff">Affise ID</th>
                    <th v-if="colOn('goId')" class="wrap oc-go">Global Offer ID</th>
                    <th v-if="colOn('vta')" class="oc-vta">VTA</th>
                    <th v-if="colOn('nm')" class="oc-nm">Name</th>
                    <th v-if="colOn('comments')" class="oc-cmt">Comments</th>
                    <th v-if="colOn('conn')" class="wrap oc-conn">Conn type</th>
                    <th v-if="colOn('weight')" class="oc-w">Weight</th>
                    <th v-if="colOn('autoWeight')" class="wrap oc-aw">Auto Weight</th>
                    <th v-if="colOn('cap')" class="oc-cap">Cap</th>
                    <th v-if="colOn('freq')" class="oc-fq">Frq</th>
                    <th v-if="colOn('ipfreq')" class="wrap oc-fq">IP Frq</th>
                    <th v-if="colOn('dfreq')" class="wrap oc-fq">D Frq</th>
                    <th v-if="colOn('subs')" class="wrap oc-subs">Subs BL / WL</th>
                    <th v-if="colOn('status')" class="wrap oc-st">Status</th>
                    <th class="oc-act"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!(current.offers || []).length">
                    <td :colspan="offerTableColspan"><div class="empty">No offers — add an offer below</div></td>
                  </tr>
                  <tr
                    v-for="o in current.offers"
                    :key="o.id"
                    :class="{ sel: selected.indexOf(o.id) !== -1, off: o.paused }"
                  >
                    <td class="oc-chk"><input class="cb" type="checkbox" :value="o.id" v-model="selected"></td>
                    <td class="oc-del"><button class="ib del" type="button" title="Delete" @click="deleteOffer(o)">🗑</button></td>
                    <td v-if="colOn('id')" class="mono oc-id">{{ o.id }}</td>
                    <td v-if="colOn('affise')" class="mono accent oc-aff">
                      <a v-if="o.affise_id" class="nlink" :href="affiseOfferHref(o.affise_id)" target="_blank" rel="noopener">{{ o.affise_id }}</a>
                      <template v-else>{{ o.affise_id || '—' }}</template>
                    </td>
                    <td v-if="colOn('goId')" class="mono oc-go">
                      <a v-if="o.global_offer_id" class="nlink" :href="globalOfferHref(o)" target="_blank" rel="noopener">{{ o.global_offer_id }}</a>
                      <template v-else>—</template>
                    </td>
                    <td v-if="colOn('vta')" class="oc-vta"><input class="cb" type="checkbox" :checked="o.vta" disabled></td>
                    <td v-if="colOn('nm')" class="nm-cell oc-nm">
                      <div class="onm" :title="o.name">
                        {{ o.name }}
                        <span v-if="o.vta" class="bdg v">VTA</span>
                      </div>
                    </td>
                    <td v-if="colOn('comments')" class="oc-cmt">
                      <input v-if="editTable" class="ci comments" :value="o.comments" @change="patchOffer(o, 'comments', $event.target.value)">
                      <span v-else class="ro-val sml">{{ o.comments || '—' }}</span>
                    </td>
                    <td v-if="colOn('conn')" class="oc-conn"><span class="ro-val sml">{{ o.connection_type || 'all' }}</span></td>
                    <td v-if="colOn('weight')" class="oc-w">
                      <div class="wcell">
                        <input v-if="editTable" class="ci w" type="number" :value="o.weight" @change="patchOffer(o, 'weight', Number($event.target.value))" @wheel.prevent>
                        <span v-else class="ro-val">{{ o.weight }}</span>
                        <span class="wpct">{{ offerWeightPct(o) }}</span>
                        <span v-if="o.auto_weight_current != null && o.auto_weight_current !== o.weight" class="wauto">→ {{ o.auto_weight_current }}</span>
                        <span v-if="o.weight < 17" class="wres">reserved</span>
                      </div>
                    </td>
                    <td v-if="colOn('autoWeight')" class="oc-aw">
                      <input class="cb" type="checkbox" :checked="o.auto_weight_enabled" :disabled="!editTable || o.weight < 17" :title="o.weight < 17 ? 'Weight under 17 is reserved — never auto-adjusted' : 'Auto weight adjustment'" @change="patchOffer(o, 'auto_weight_enabled', $event.target.checked)">
                    </td>
                    <td v-if="colOn('cap')" class="oc-cap">
                      <input v-if="editTable" class="ci cap" type="number" :value="o.cap" @change="patchOffer(o, 'cap', Number($event.target.value))" @wheel.prevent>
                      <span v-else class="ro-val">{{ fmtNum(o.cap) }}</span>
                    </td>
                    <td v-if="colOn('freq')" class="oc-fq">
                      <select v-if="editTable" class="cs fq" :value="o.frequency" @change="patchOffer(o, 'frequency', Number($event.target.value))">
                        <option v-for="f in freq" :key="'of'+f.value+o.id" :value="f.value">{{ f.text }}</option>
                      </select>
                      <span v-else class="ro-val">{{ freqLbl(o.frequency) }}</span>
                    </td>
                    <td v-if="colOn('ipfreq')" class="oc-fq">
                      <select v-if="editTable" class="cs fq" :value="o.ip_frequency" @change="patchOffer(o, 'ip_frequency', Number($event.target.value))">
                        <option v-for="f in freq" :key="'ip'+f.value+o.id" :value="f.value">{{ f.text }}</option>
                      </select>
                      <span v-else class="ro-val">{{ freqLbl(o.ip_frequency) }}</span>
                    </td>
                    <td v-if="colOn('dfreq')" class="oc-fq">
                      <select v-if="editTable" class="cs fq" :value="o.delivery_frequency" @change="patchOffer(o, 'delivery_frequency', Number($event.target.value))">
                        <option v-for="f in freq" :key="'df'+f.value+o.id" :value="f.value">{{ f.text }}</option>
                      </select>
                      <span v-else class="ro-val">{{ freqLbl(o.delivery_frequency) }}</span>
                    </td>
                    <td v-if="colOn('subs')" class="oc-subs">
                      <span v-if="subList(o, 'blacklist').length" class="subn bl" :title="'Blacklist\n' + subTip(o, 'blacklist')">{{ subList(o, 'blacklist').join(',') }}</span>
                      <span v-if="subList(o, 'whitelist').length" class="subn wl" :title="'Whitelist\n' + subTip(o, 'whitelist')">{{ subList(o, 'whitelist').join(',') }}</span>
                      <span v-if="!subList(o, 'blacklist').length && !subList(o, 'whitelist').length" class="ro-val muted">—</span>
                    </td>
                    <td v-if="colOn('status')" class="ost oc-st">
                      <div v-if="!o.paused" class="pause-row">
                        <button class="pill a" type="button" :disabled="!slServing" :title="slServing ? '' : 'Smartlink is paused'" @click="setOfferPause(o, true)">Active</button>
                      </div>
                      <template v-else>
                        <button class="pill p" type="button" :disabled="!slServing" :title="slServing ? '' : 'Smartlink is paused'" @click="setOfferPause(o, false)">Paused</button>
                        <div v-if="pauseLeft(o)" class="pause-left">{{ pauseLeft(o) }}</div>
                      </template>
                      <div v-if="showOfferNsrv(o)" class="nsrv" :title="o.not_serving_reason">not serving</div>
                    </td>
                    <td class="oc-act">
                      <button class="ib" type="button" title="Full setup" @click="openOffer = o">⤢</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="qadd">
              <span class="lb">Add</span>
              <button class="btn btn-g btn-s" type="button" @click="showAssoc = true">Add Offer</button>
              <span class="hint">associate a global offer or create a manual one</span>
            </div>
            <div class="oacts">
              <span class="grow"></span>
              <button class="btn btn-g btn-s" type="button" @click="bulkAuto(false)">Turn off auto adjustment for all offers</button>
              <button class="btn btn-g btn-s" type="button" @click="bulkAuto(true)">Turn on for all</button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div class="bulk" :class="{ on: selected.length }">
      <span class="bkn">{{ selected.length }} selected</span>
      <div class="bks"></div>
      <div class="bkf">
        <label>Weight</label>
        <input placeholder="—" @change="bulkField('weight', Number($event.target.value)); $event.target.value=''">
      </div>
      <div class="bkf">
        <label>Cap</label>
        <input placeholder="—" @change="bulkField('cap', Number($event.target.value)); $event.target.value=''">
      </div>
      <div class="bks"></div>
      <button class="bki" type="button" title="Activate" @click="bulkField('paused', false)">▶</button>
      <button class="bki" type="button" title="Pause" @click="bulkField('paused', true)">❚❚</button>
      <button class="bkx" type="button" @click="selected = []">Clear</button>
    </div>

    <div class="ov" :class="{ on: !!openOffer || showActivity }" @click="onOverlay"></div>
    <OfferDrawer
      v-if="openOffer"
      :offer="openOffer"
      :ad-set="current || {}"
      :selected-ids="selected"
      :camp-id="tree.id"
      @patch="onDrawerPatch"
      @sub-rules="markSubRules('offer', openOffer.id)"
      @prev="stepOffer(-1)"
      @next="stepOffer(1)"
      @apply-selected="applyDrawerToSelected"
      @close="openOffer = null"
    />
    <ActivityDrawer
      v-if="showActivity && tree"
      :camp-id="tree.id"
      @close="showActivity = false"
    />

    <FrqModal :is-show.sync="showFrqLegend" />
    <GlobalOfferAssocModal
      :isShow.sync="showAssoc"
      :placement="current || {}"
      :camp-id="tree && tree.id"
      :isVtaRoute="false"
      @shouldUpdate="loadTree"
      @created="onManualOfferCreated"
    />
    <div v-if="pop" ref="lossPop" class="pop" :style="popStyle" @mouseenter="keepPop" @mouseleave="hidePopSoon">
      <h4>{{ tree && tree.name }}</h4>
      <div class="psub">Loss breakdown</div>
      <div v-if="!popGroups.length" class="psub">No loss reasons in range</div>
      <div v-for="g in popGroups" :key="g.label">
        <div class="pgrp">{{ g.label }}</div>
        <div v-for="k in g.keys" :key="k.key" class="prow2">
          <span>{{ k.label }}</span><span>{{ fmtNum(st[k.key]) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SlV2Status from '../components/v2/SlV2Status.vue'
import FreqSeg from '../components/v2/FreqSeg.vue'
import SubRulesEditor from '../components/v2/SubRulesEditor.vue'
import OfferDrawer from '../components/v2/OfferDrawer.vue'
import ActivityDrawer from '../components/v2/ActivityDrawer.vue'
import GeoRegionSelect from '../components/v2/GeoRegionSelect.vue'
import FrqModal from '../../../../components/FrqModal.vue'
import GlobalOfferAssocModal from '../components/v2/AssocGlobalOfferToAdSetModal.vue'
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import { FREQ_OPTIONS, IP_FRQ_LEVELS, fmtNum, fmtPct, subChips, freqLbl, ipLevelLbl, campaignLink, ruleValCount, payloadSubRules, trimSubRules, pauseToMinutes, pauseReleaseLeft, visibleLossGroups, placeLossPop, geoCode, regionGeoOk, osFlags, osFromFlags, connFlags, connFromFlags, osSum, connSum, trafficSum, creativeSum } from '../v2/status'
import { OFFER_TABLE_COLS, loadOfferCols, saveOfferCols } from '../v2/offer-cols'
import moment from 'moment'
import '../v2/sl-v2.css'

export default {
  components: { SlV2Status, FreqSeg, SubRulesEditor, OfferDrawer, ActivityDrawer, GeoRegionSelect, FrqModal, GlobalOfferAssocModal, DateRangePicker },
  data() {
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    const today = new Date()
    return {
      tree: null,
      original: null,
      picker: [],
      pickerId: 0,
      pickerOpen: false,
      pickerQ: '',
      groupList: [{ id: 0, name: 'none' }],
      currentId: 0,
      openSetup: false,
      openAdset: false,
      railCollapsed: false,
      railDragSrc: null,
      orderDirty: false,
      editTable: false,
      colMenuOpen: false,
      offerColDefs: OFFER_TABLE_COLS,
      offerCols: loadOfferCols(),
      saving: false,
      statsLoading: false,
      loadedStatKey: '',
      dateRange: { startDate: yesterday, endDate: yesterday },
      maxDate: new Date(),
      ranges: {
        Today: [today, today],
        Yesterday: [yesterday, yesterday],
        'Last 7 days': [new Date(Date.now() - 24 * 7 * 3600 * 1000), yesterday]
      },
      showAssoc: false,
      showFrqLegend: false,
      showActivity: false,
      openOffer: null,
      selected: [],
      dirtySL: {},
      dirtyAS: {},
      dirtyOffers: {},
      dirtySubs: {},
      bundlesLists: [],
      useBundlesOpen: {},
      freq: FREQ_OPTIONS,
      ipLevels: IP_FRQ_LEVELS,
      nowMs: Date.now(),
      pauseClock: null,
      pop: false,
      popStyle: {},
      popAnchor: null,
      hidePopTimer: null
    }
  },
  computed: {
    current() {
      if (!this.tree) return null
      return this.tree.ad_sets.find(a => a.id === this.currentId) || this.tree.ad_sets[0] || null
    },
    statusModel() {
      const t = this.tree
      return {
        id: t.id,
        name: t.name,
        status: t.status,
        forced: t.forced,
        group_forced: t.forced,
        group_id: t.group_id,
        scheduled: t.scheduled,
        snapshot_placements: t.snapshot_placements,
        active_offers: t.active_offers
      }
    },
    dirtyCount() {
      return Object.keys(this.dirtySL).length
        + Object.keys(this.dirtyAS).length
        + Object.keys(this.dirtyOffers).length
        + Object.keys(this.dirtySubs).length
        + (this.orderDirty ? 1 : 0)
    },
    pickerLabel() {
      const p = (this.picker || []).find(x => Number(x.id) === Number(this.pickerId))
      if (p) return p.id + ' · ' + (p.name || '')
      if (this.tree) return this.tree.id + ' · ' + (this.tree.name || '')
      return ''
    },
    currentPickerName() {
      const p = (this.picker || []).find(x => Number(x.id) === Number(this.pickerId))
      if (p && p.name) return p.name
      return (this.tree && this.tree.name) || ''
    },
    filteredPicker() {
      const q = String(this.pickerQ || '').trim().toLowerCase()
      const list = this.picker || []
      if (!q) return list
      return list.filter(p => String(p.id).includes(q) || String(p.name || '').toLowerCase().includes(q))
    },
    campLink() {
      return campaignLink(this.tree.id)
    },
    allOffersSelected() {
      const list = (this.current && this.current.offers) || []
      return list.length > 0 && list.every(o => this.selected.indexOf(o.id) !== -1)
    },
    st() {
      return (this.tree && this.tree.stats) || {}
    },
    popGroups() {
      return this.pop ? visibleLossGroups(this.st) : []
    },
    statRangeParams() {
      return {
        from: moment(this.dateRange.startDate).format('YYYY-MM-DD'),
        to: moment(this.dateRange.endDate).format('YYYY-MM-DD')
      }
    },
    setupRuleStats() {
      const rules = (this.tree && this.tree.sub_rules) || []
      const vals = rules.reduce((n, r) => n + ruleValCount(r), 0)
      return { n: rules.length, vals }
    },
    canEditBundles() {
      return this.userHasPermission('bundles.assoc')
    },
    slServing() {
      return String((this.tree && this.tree.effective_status) || '').toLowerCase() === 'active'
    },
    offerTableColspan() {
      let n = 3
      this.offerColDefs.forEach(c => { if (this.offerCols[c.k]) n++ })
      return n
    }
  },
  async created() {
    this.pickerId = Number(this.$route.params.campId)
    await this.loadGroups()
    await this.loadPicker()
    await this.loadBundlesLists()
    await this.loadTree()
    window.addEventListener('keydown', this.onKey)
    window.addEventListener('beforeunload', this.onBeforeUnload)
    document.addEventListener('click', this.closeColMenu)
    this.pauseClock = setInterval(() => { this.nowMs = Date.now() }, 1000)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKey)
    window.removeEventListener('beforeunload', this.onBeforeUnload)
    document.removeEventListener('click', this.closeColMenu)
    clearInterval(this.pauseClock)
    clearTimeout(this.hidePopTimer)
  },
  beforeRouteLeave(to, from, next) {
    if (this.confirmUnsavedLeave()) {
      this._leaveOk = true
      next()
    } else {
      this.pickerId = Number(from.params.campId)
      next(false)
    }
  },
  watch: {
    '$route.params.campId'(v, oldV) {
      const nextId = Number(v)
      if (this._skipCampWatch) {
        this._skipCampWatch = false
        this.pickerId = nextId
        return
      }
      if (this._leaveOk || this.confirmUnsavedLeave()) {
        this._leaveOk = false
        this.pickerId = nextId
        this.loadTree()
        return
      }
      this._skipCampWatch = true
      this.pickerId = Number(oldV)
      this.$router.replace({ name: 'SmartLinksV2Workspace', params: { campId: oldV } })
    }
  },
  methods: {
    fmtNum, fmtPct, freqLbl, ipLevelLbl, geoCode, regionGeoOk, osFlags, connFlags, osSum, connSum, trafficSum, creativeSum,
    chip(rules, mode) {
      const c = subChips(rules)
      return mode === 'blacklist' ? c.bl.join(',') : c.wl.join(',')
    },
    subList(o, mode) {
      const c = subChips(o && o.sub_rules)
      return mode === 'blacklist' ? c.bl : c.wl
    },
    subTip(o, mode) {
      return (o.sub_rules || [])
        .filter(r => (mode === 'whitelist' ? r.mode === 'whitelist' : r.mode !== 'whitelist'))
        .map(r => r.sub + ' — ' + ruleValCount(r) + ' values')
        .join('\n')
    },
    offerWeightPct(o) {
      const t = this.asWeightTotal(this.current)
      if (!t || o.paused) return '0.0%'
      return ((Number(o.weight) || 0) / t * 100).toFixed(1) + '%'
    },
    affiseOfferHref(id) {
      return id ? 'https://offers-apptimus.affise.com/cpa/offer/' + id : ''
    },
    globalOfferHref(o) {
      const id = Number(o && o.global_offer_id)
      if (!id) return ''
      if (o.is_new_global_offer || id >= 40000) {
        return '/apps/smartlinks/new-global-offers/create?id=' + id
      }
      return '/apps/smartlinks/global-offers-edit?id=' + id + '&isVta=' + (o.vta ? 1 : 0)
    },
    colOn(k) {
      return !!this.offerCols[k]
    },
    setOfferCol(k, on) {
      this.$set(this.offerCols, k, !!on)
      saveOfferCols(this.offerCols)
    },
    closeColMenu(e) {
      const t = e && e.target
      if (this.colMenuOpen && !(t && t.closest && t.closest('.colsel'))) this.colMenuOpen = false
      if (this.pickerOpen && !(t && t.closest && t.closest('.sl-pick'))) this.closePicker()
    },
    togglePicker() {
      this.pickerOpen = !this.pickerOpen
      if (this.pickerOpen) {
        this.pickerQ = ''
        this.$nextTick(() => {
          if (this.$refs.pickerSearch) this.$refs.pickerSearch.focus()
        })
      }
    },
    closePicker() {
      this.pickerOpen = false
      this.pickerQ = ''
    },
    chooseSL(id) {
      this.pickerId = Number(id)
      this.closePicker()
      this.switchSL()
    },
    toggleAllOffers(e) {
      const list = (this.current && this.current.offers) || []
      this.selected = e.target.checked ? list.map(o => o.id) : []
    },
    weightLabel(o) {
      if (o.auto_weight_current && o.auto_weight_current !== o.weight) {
        return `${o.weight} → ${o.auto_weight_current}`
      }
      return String(o.weight)
    },
    confirmUnsavedLeave() {
      if (!this.dirtyCount) return true
      return window.confirm('You have unsaved changes. Leave anyway?')
    },
    onBeforeUnload(e) {
      if (!this.dirtyCount) return
      e.preventDefault()
      e.returnValue = ''
    },
    onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        this.save()
      }
      if (e.key === 'e' || e.key === 'E') {
        if (e.target && ['INPUT', 'SELECT', 'TEXTAREA'].includes(e.target.tagName)) return
        this.editTable = !this.editTable
      }
      if (e.key === 'a' || e.key === 'A') {
        if (e.target && ['INPUT', 'SELECT', 'TEXTAREA'].includes(e.target.tagName)) return
        this.openAdset = !this.openAdset
      }
      if (e.key === '[') {
        if (e.target && ['INPUT', 'SELECT', 'TEXTAREA'].includes(e.target.tagName)) return
        this.railCollapsed = !this.railCollapsed
      }
      if (e.key === 'j' || e.key === 'J') this.stepAdSet(1)
      if (e.key === 'k' || e.key === 'K') this.stepAdSet(-1)
      if (e.key === 'Escape') {
        this.colMenuOpen = false
        this.closePicker()
        if (this.showActivity) {
          this.showActivity = false
          return
        }
        this.openOffer = null
        this.showAssoc = false
      }
      if (this.openOffer && e.key === 'ArrowDown') {
        e.preventDefault()
        this.stepOffer(1)
      }
      if (this.openOffer && e.key === 'ArrowUp') {
        e.preventDefault()
        this.stepOffer(-1)
      }
    },
    stepOffer(d) {
      if (!this.current || !this.openOffer) return
      const list = this.current.offers || []
      const i = list.findIndex(o => o.id === this.openOffer.id)
      const n = list[i + d]
      if (n) this.openOffer = n
    },
    onDrawerPatch(field, value) {
      if (!this.openOffer) return
      this.patchOffer(this.openOffer, field, value)
    },
    applyDrawerToSelected() {
      if (!this.openOffer) return
      const src = this.openOffer
      const fields = ['weight', 'auto_weight_enabled', 'cap', 'frequency', 'ip_frequency', 'ip_frequency_level', 'delivery_frequency', 'connection_type', 'os_version', 'traffic_in_app', 'traffic_mobile', 'creative_tag', 'creative_img', 'is_device_id_traffic', 'pid', 'subs_query', 'custom_url', 'comments', 'advertiser_requests']
      this.selected.forEach(id => {
        if (id === src.id) return
        const o = this.findOffer(id)
        if (!o.id) return
        fields.forEach(f => this.patchOffer(o, f, src[f]))
        o.sub_rules = JSON.parse(JSON.stringify(src.sub_rules || []))
        this.markSubRules('offer', o.id)
      })
      this.notifySuccess('Applied to ' + this.selected.length + ' offers')
    },
    stepAdSet(d) {
      if (!this.tree) return
      const ids = this.tree.ad_sets.map(a => a.id)
      const i = Math.max(0, ids.indexOf(this.currentId))
      this.currentId = ids[(i + d + ids.length) % ids.length]
    },
    moveAdSet(i, d) {
      const j = i + d
      if (!this.tree || j < 0 || j >= this.tree.ad_sets.length) return
      this.reorderAdSets(i, j)
    },
    asOffers(as) {
      return (as && as.offers) || []
    },
    asOfferCount(as) {
      return this.asOffers(as).length
    },
    asActiveCount(as) {
      return this.asOffers(as).filter(o => !o.paused).length
    },
    asWeightTotal(as) {
      return this.asOffers(as).reduce((n, o) => n + (Number(o.weight) || 0), 0)
    },
    asAutoCount(as) {
      return this.asOffers(as).filter(o => o.auto_weight_enabled).length
    },
    asSubCount(as) {
      return this.asOffers(as).filter(o => (o.sub_rules || []).length).length
    },
    asRailTitle(as, idx) {
      const n = this.asOfferCount(as)
      return `${idx + 1}. ${as.name}\n${as.id} · ${as.geo || '—'} · ${as.os || '—'}\n${this.asActiveCount(as)}/${n} active · weight ${this.asWeightTotal(as)}`
    },
    onRailDrag(idx) {
      this.railDragSrc = idx
    },
    onRailDrop(idx) {
      const src = this.railDragSrc
      this.railDragSrc = null
      if (src == null || src === idx) return
      this.reorderAdSets(src, idx)
    },
    reorderAdSets(from, to) {
      if (!this.tree || from === to) return
      const arr = this.tree.ad_sets.slice()
      const [moved] = arr.splice(from, 1)
      arr.splice(to, 0, moved)
      this.tree.ad_sets = arr
      this.currentId = moved.id
      this.orderDirty = true
    },
    clearDirty() {
      this.dirtySL = {}
      this.dirtyAS = {}
      this.dirtyOffers = {}
      this.dirtySubs = {}
      this.orderDirty = false
      this.useBundlesOpen = {}
    },
    asUsesBundles(as) {
      if (!as) return false
      if (as.bundles_lists_id) return true
      return !!this.useBundlesOpen[as.id]
    },
    toggleASBundles(as, on) {
      if (on) {
        this.$set(this.useBundlesOpen, as.id, true)
        return
      }
      this.$set(this.useBundlesOpen, as.id, false)
      this.patchAS(as, 'bundles_lists_id', 0)
    },
    async loadBundlesLists() {
      try {
        const r = await this.$http.post(this.resources.Management.getBundlesLists, {})
        const body = r.body || await r.json()
        const lists = (body && body.data) || []
        this.bundlesLists = lists.slice().sort((a, b) => (a.list_name || '').localeCompare(b.list_name || ''))
      } catch (e) {
        this.bundlesLists = []
      }
    },
    async loadGroups() {
      const r = await this.$http.get(this.resources.SmartLinksCampaigns.v2Groups)
      this.groupList = await r.json()
    },
    async loadPicker() {
      const r = await this.$http.get(this.resources.SmartLinksCampaigns.v2Picker)
      this.picker = await r.json()
      this.ensureCurrentInPicker()
    },
    ensureCurrentInPicker() {
      if (!this.tree) return
      const id = Number(this.tree.id)
      if ((this.picker || []).some(p => Number(p.id) === id)) return
      this.picker = [{ id: this.tree.id, name: this.tree.name, status: this.tree.status }, ...(this.picker || [])]
    },
    onStatRangeUpdate(range) {
      if (range && range.startDate) {
        this.dateRange = {
          startDate: range.startDate,
          endDate: range.endDate || range.startDate
        }
      }
      this.refreshStats()
    },
    async refreshStats() {
      if (!this.tree) return
      this.statsLoading = true
      try {
        const r = await this.$http.get(this.resources.SmartLinksCampaigns.v2Stats(this.tree.id), { params: this.statRangeParams })
        this.$set(this.tree, 'stats', await r.json())
        this.loadedStatKey = this.statRangeParams.from + ':' + this.statRangeParams.to
      } catch (e) {
        this.notifyError('Could not load statistics')
      }
      this.statsLoading = false
    },
    keepPop() {
      clearTimeout(this.hidePopTimer)
    },
    hidePopSoon() {
      clearTimeout(this.hidePopTimer)
      this.hidePopTimer = setTimeout(() => { this.pop = false; this.popAnchor = null }, 120)
    },
    showPop(e) {
      this.keepPop()
      this.pop = true
      this.popAnchor = e.currentTarget || e.target
      this.popStyle = { position: 'fixed', visibility: 'hidden', top: '0px', left: '0px' }
      this.$nextTick(() => {
        if (!this.pop || !this.popAnchor) return
        this.popStyle = placeLossPop(this.popAnchor, this.$refs.lossPop)
      })
    },
    async generateReport(type) {
      try {
        const res = await this.$http.post(this.resources.SmartLinksCampaigns.newSlRequestsBlockedReport(this.tree.id), undefined, {
          params: {
            date: this.statRangeParams.from,
            end_date: this.statRangeParams.to,
            request: type === 'requests' ? 'true' : 'false'
          }
        })
        if (res.ok) this.notifySuccess('The report will be sent to your email when ready', 10000)
        else this.notifyError('Could not generate report')
      } catch (e) {
        this.notifyError('Could not generate report')
      }
    },
    async onManualOfferCreated(offerId) {
      await this.loadTree()
      const o = this.findOffer(offerId)
      if (o && o.id) this.openOffer = o
    },
    async loadTree() {
      const keepAs = this.currentId
      const keepOffer = this.openOffer && this.openOffer.id
      const id = this.pickerId || this.$route.params.campId
      const r = await this.$http.get(this.resources.SmartLinksCampaigns.v2Tree(id), { params: this.statRangeParams })
      this.tree = this.normalizeTree(await r.json())
      this.original = JSON.parse(JSON.stringify(this.tree))
      if (keepAs && this.tree.ad_sets.some(a => a.id === keepAs)) this.currentId = keepAs
      else this.currentId = (this.tree.ad_sets[0] && this.tree.ad_sets[0].id) || 0
      this.openOffer = keepOffer ? this.findOffer(keepOffer) : null
      if (this.openOffer && !this.openOffer.id) this.openOffer = null
      this.clearDirty()
      this.selected = []
      this.openSetup = false
      this.openAdset = false
      this.loadedStatKey = this.statRangeParams.from + ':' + this.statRangeParams.to
      this.ensureCurrentInPicker()
    },
    normalizeTree(tree) {
      if (!tree.stats) tree.stats = {}
      if (!tree.ad_sets) tree.ad_sets = []
      if (!tree.sub_rules) tree.sub_rules = []
      this.ensureRuleExtras(tree.sub_rules)
      if (!tree.ip_frequency_level) tree.ip_frequency_level = 'l3'
      tree.ad_sets.forEach(as => {
        if (!as.offers) as.offers = []
        as.geo = geoCode(as.geo)
        as.region = String(as.region || '').replace(/^!/, '')
        if (!as.ip_frequency_level) as.ip_frequency_level = 'l3'
        if (!as.connection_type) as.connection_type = 'all'
        if (as.traffic_in_app == null) as.traffic_in_app = 1
        if (as.traffic_mobile == null) as.traffic_mobile = 1
        if (as.creative_tag == null) as.creative_tag = 1
        if (as.creative_img == null) as.creative_img = 1
        if (as.bundles_lists_id == null) as.bundles_lists_id = 0
        as.use_offer_rotation = !!as.use_offer_rotation
        as.offers.forEach(o => {
          if (!o.sub_rules) o.sub_rules = []
          this.ensureRuleExtras(o.sub_rules)
          if (!o.bl_files) o.bl_files = []
          if (!o.ip_frequency_level) o.ip_frequency_level = 'l3'
          if (!o.connection_type) o.connection_type = 'all'
          if (o.traffic_in_app == null) o.traffic_in_app = 1
          if (o.traffic_mobile == null) o.traffic_mobile = 1
          if (o.creative_tag == null) o.creative_tag = 1
          if (o.creative_img == null) o.creative_img = 1
          if (o.region) o.region = String(o.region).replace(/^!/, '')
        })
      })
      return tree
    },
    async copyCampLink() {
      const link = this.campLink
      try {
        await navigator.clipboard.writeText(link)
      } catch (e) {
        const el = document.createElement('textarea')
        el.value = link
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
      }
      this.notifySuccess('Campaign link copied')
    },
    switchSL() {
      this.$router.push({ name: 'SmartLinksV2Workspace', params: { campId: this.pickerId } })
    },
    patchSL(field, value) {
      this.tree[field] = value
      this.$set(this.dirtySL, field, value)
    },
    patchAS(as, field, value) {
      as[field] = value
      const cur = this.dirtyAS[as.id] || { id: as.id }
      cur[field] = value
      this.$set(this.dirtyAS, as.id, cur)
    },
    onGeoChange(val) {
      const prev = geoCode(this.current && this.current.geo)
      const next = geoCode(val)
      this.patchAS(this.current, 'geo', val)
      if (prev !== next) this.patchAS(this.current, 'region', '')
    },
    toggleASOs(which) {
      const f = osFlags(this.current.os)
      const next = { ios: f.ios, android: f.android }
      next[which] = !next[which]
      if (!next.ios && !next.android) return
      this.patchAS(this.current, 'os', osFromFlags(next.ios, next.android))
    },
    toggleASConn(which) {
      const f = connFlags(this.current.connection_type)
      const next = { wifi: f.wifi, cell: f.cell }
      next[which] = !next[which]
      if (!next.wifi && !next.cell) return
      this.patchAS(this.current, 'connection_type', connFromFlags(next.wifi, next.cell))
    },
    toggleASBit(field, other) {
      const on = !!this.current[field]
      if (on && !this.current[other]) return
      this.patchAS(this.current, field, on ? 0 : 1)
    },
    patchOffer(o, field, value) {
      o[field] = value
      const cur = this.dirtyOffers[o.id] || { id: o.id }
      cur[field] = value
      this.$set(this.dirtyOffers, o.id, cur)
    },
    ensureRuleExtras(rules) {
      ;(rules || []).forEach(r => {
        if (!Array.isArray(r.extras)) this.$set(r, 'extras', [])
      })
    },
    markSubRules(ownerType, ownerId) {
      const rules = ownerType === 'smart_link' ? this.tree.sub_rules : (this.openOffer && this.openOffer.id === ownerId ? this.openOffer.sub_rules : this.findOffer(ownerId).sub_rules)
      this.ensureRuleExtras(rules)
      this.$set(this.dirtySubs, ownerType + ':' + ownerId, {
        owner_type: ownerType,
        owner_id: ownerId,
        rules: payloadSubRules(rules)
      })
    },
    trimDirtySubRules() {
      if (!this.tree) return
      trimSubRules(this.tree.sub_rules)
      ;(this.tree.ad_sets || []).forEach(as => {
        (as.offers || []).forEach(o => trimSubRules(o.sub_rules))
      })
      Object.keys(this.dirtySubs).forEach(k => {
        const set = this.dirtySubs[k]
        const live = set.owner_type === 'smart_link'
          ? this.tree.sub_rules
          : (this.openOffer && this.openOffer.id === set.owner_id ? this.openOffer.sub_rules : this.findOffer(set.owner_id).sub_rules)
        set.rules = payloadSubRules(live)
      })
    },
    findOffer(id) {
      for (const as of this.tree.ad_sets) {
        const o = as.offers.find(x => x.id === id)
        if (o) return o
      }
      return { sub_rules: [] }
    },
    showOfferNsrv(o) {
      if (!o || !o.not_serving_reason) return false
      if (!this.slServing) return false
      const parent = (this.tree && this.tree.blocked_reason) || ''
      return o.not_serving_reason !== parent
    },
    setOfferPause(o, paused) {
      if (!this.slServing) return
      if (paused) {
        this.patchOffer(o, 'pause_duration_minutes', pauseToMinutes(1, 'Hours'))
        this.$set(o, 'pause_ts', new Date().toISOString())
        this.patchOffer(o, 'paused', true)
      } else {
        this.patchOffer(o, 'pause_duration_minutes', 0)
        this.patchOffer(o, 'paused', false)
      }
    },
    pauseLeft(o) {
      return pauseReleaseLeft(o, this.nowMs)
    },
    bulkField(field, value) {
      this.selected.forEach(id => {
        const o = this.findOffer(id)
        if (field === 'paused') {
          this.setOfferPause(o, value)
          return
        }
        this.patchOffer(o, field, value)
      })
    },
    async save() {
      if (!this.dirtyCount || this.saving) return
      this.saving = true
      this.trimDirtySubRules()
      const body = {
        smart_link: Object.keys(this.dirtySL).length ? this.dirtySL : undefined,
        ad_sets: Object.values(this.dirtyAS),
        offers: Object.values(this.dirtyOffers),
        ad_set_order: this.tree.ad_sets.map(a => a.id),
        sub_rules: Object.values(this.dirtySubs)
      }
      try {
        const keepOffer = this.openOffer && this.openOffer.id
        const keepAs = this.currentId
        const r = await this.$http.patch(this.resources.SmartLinksCampaigns.v2Batch(this.tree.id), body)
        this.tree = this.normalizeTree(await r.json())
        this.original = JSON.parse(JSON.stringify(this.tree))
        this.currentId = keepAs
        this.openOffer = keepOffer ? this.findOffer(keepOffer) : null
        if (this.openOffer && !this.openOffer.id) this.openOffer = null
        this.clearDirty()
        this.notifySuccess('Saved')
      } catch (err) {
        if (err.status === 409) {
          this.notifyError('Someone else changed this smart link. Reloading.')
          await this.loadTree()
        } else if (err.status === 422) {
          this.notifyError('Validation failed. Check frequency and sub rules.')
        } else {
          this.notifyError('Save failed')
        }
      }
      this.saving = false
    },
    undo() {
      const keepOffer = this.openOffer && this.openOffer.id
      this.tree = JSON.parse(JSON.stringify(this.original))
      this.clearDirty()
      this.openOffer = keepOffer ? this.findOffer(keepOffer) : null
      if (this.openOffer && !this.openOffer.id) this.openOffer = null
    },
    openActivity() {
      this.showActivity = true
    },
    onOverlay() {
      if (this.showActivity) {
        this.showActivity = false
        return
      }
      this.openOffer = null
    },
    async setGroup(e) {
      const groupId = Number(e.target.value)
      await this.$http.put(this.resources.SmartLinksCampaigns.setCampaignGroup(this.tree.id), { group_id: groupId })
      this.loadTree()
    },
    async createAdSet() {
      await this.$http.post(this.resources.SmartLinksCampaigns.v2CreateAdSet(this.tree.id))
      await this.loadTree()
    },
    async duplicateAdSet() {
      if (!this.current) return
      await this.$http.post(this.resources.SmartLinksCampaigns.v2DuplicateAdSet(this.tree.id, this.current.id))
      await this.loadTree()
    },
    async deleteAdSet() {
      if (!this.current || !window.confirm('Delete this ad set?')) return
      await this.$http.delete(this.resources.SmartLinksCampaigns.v2DeleteAdSet(this.tree.id, this.current.id))
      await this.loadTree()
    },
    async deleteOffer(o) {
      if (!window.confirm('Delete offer ' + o.id + '?')) return
      await this.$http.delete(this.resources.SmartLinksCampaigns.v2DeleteOffer(o.id))
      await this.loadTree()
    },
    async bulkAuto(enabled) {
      if (!this.current) return
      await this.$http.post(this.resources.SmartLinksCampaigns.v2BulkAutoWeight(this.tree.id, this.current.id), { enabled })
      await this.loadTree()
    }
  }
}
</script>
